import { useState } from 'react';
import { GripVertical } from 'lucide-react';
import type { SupportedSymbol } from '../../lib/symbols';

interface AdminSymbolRowProps {
  symbol: SupportedSymbol;
  isDragging: boolean;
  onToggleEnabled: (ticker: string, enabled: boolean) => Promise<void>;
  onDragStart: (ticker: string) => void;
  onDragEnter: (ticker: string) => void;
  onDragEnd: () => void;
}

export function AdminSymbolRow({
  symbol,
  isDragging,
  onToggleEnabled,
  onDragStart,
  onDragEnter,
  onDragEnd,
}: AdminSymbolRowProps) {
  const [isBusy, setIsBusy] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const handleToggle = async () => {
    setIsBusy(true);
    setRowError(null);
    try {
      await onToggleEnabled(symbol.ticker, !symbol.enabled);
    } catch (error) {
      setRowError(error instanceof Error ? error.message : 'Update failed');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div
      draggable
      onDragStart={(event) => {
        event.dataTransfer?.setData('text/plain', symbol.ticker);
        if (event.dataTransfer != null) {
          event.dataTransfer.effectAllowed = 'move';
        }
        onDragStart(symbol.ticker);
      }}
      onDragEnter={() => onDragEnter(symbol.ticker)}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        onDragEnd();
      }}
      onDragEnd={onDragEnd}
      className={`border-b border-white/[.05] transition-opacity duration-150 last:border-b-0 ${
        isDragging ? 'opacity-40' : ''
      }`}
    >
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-3 py-2.5">
        <span
          aria-hidden="true"
          className="cursor-grab text-neutral-600 active:cursor-grabbing"
          title="Drag to reorder"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </span>

        <span
          className={`truncate font-mono text-[12px] font-semibold tracking-[0.03em] ${
            symbol.enabled ? 'text-neutral-100' : 'text-neutral-600'
          }`}
        >
          {symbol.ticker}
        </span>

        <button
          type="button"
          role="switch"
          aria-checked={symbol.enabled}
          aria-label={`${symbol.enabled ? 'Disable' : 'Enable'} ${symbol.ticker}`}
          disabled={isBusy}
          onClick={() => void handleToggle()}
          className={`relative h-4 w-8 shrink-0 rounded-full border transition-colors duration-150 disabled:cursor-wait disabled:opacity-50 ${
            symbol.enabled
              ? 'border-green-500/40 bg-green-500/25'
              : 'border-white/[.1] bg-white/[.05]'
          }`}
        >
          <span
            className={`absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full transition-all duration-150 ${
              symbol.enabled
                ? 'left-[17px] bg-green-400 shadow-[0_0_4px_rgba(74,222,128,0.5)]'
                : 'left-[3px] bg-zinc-500'
            }`}
          />
        </button>
      </div>

      {rowError != null && (
        <p className="px-3 pb-2 font-mono text-[10px] text-red-400">{rowError}</p>
      )}
    </div>
  );
}
